<template>
  <modal
    :open="open"
    :title="`${materialName} - Sourcing Details`"
    :closeOnOutsideClick="true"
    @close="closeModal"
  >
    <div class="inventory-modal-content">
      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <p>Loading inventory details...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="!inventoryData || inventoryData.length === 0" class="empty-state">
        <p>No sourcing details available for this material</p>
      </div>

      <!-- Data Table -->
      <div v-else class="copy-dtx-table">
        <table>
          <thead>
            <tr>
              <th>PO NUMBER</th>
              <th>SUPPLIER</th>
              <th>QUANTITY</th>
              <th>DATE RECEIVED</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in inventoryData" :key="index" @click="openFormDetails(item)" class="clickable-row">
              <td v-overflow-tooltip :title="item.po || '--'">
                {{ item.po || '--' }}
              </td>
              <td v-overflow-tooltip :title="item.supplier || '--'">
                {{ item.supplier || '--' }}
              </td>
              <td v-overflow-tooltip :title="item.quantity || '--'">
                {{ item.quantity || '--' }}
              </td>
              <td v-overflow-tooltip :title="item.dateReceived || '--'">
                {{ item.dateReceived || '--' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Total Quantity -->
      <div v-if="inventoryData && inventoryData.length > 0" class="total-quantity-section">
        <div class="total-quantity-box">
          <span class="total-label">Total Quantity:</span>
          <span class="total-value">{{ totalQuantity }}</span>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import Modal from '../common/modal.vue'
import { getMaterialInventoryDetails } from '@/api'
import { genericFormatDate } from '@/utils/date'
import Config from '@/config'

export default {
  name: 'MaterialInventoryModal',
  components: {
    Modal
  },
  filters: {
    genericFormatDate
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    materialId: {
      type: String,
      default: null
    },
    materialName: {
      type: String,
      default: 'Material'
    },
    totalQuantity: {
      type: [Number, String],
      default: 0
    }
  },
  data () {
    return {
      isLoading: false,
      inventoryData: []
    }
  },
  watch: {
    open (newVal) {
      if (newVal && this.materialId) {
        this.fetchInventoryDetails()
      }
    }
  },
  methods: {
    async fetchInventoryDetails () {
      this.isLoading = true
      try {
        const response = await getMaterialInventoryDetails(this.materialId)
        this.inventoryData = this.mapInventoryData(response.core_forms || [])
      } catch (error) {
        console.error('Error fetching inventory details:', error)
        this.inventoryData = []
      } finally {
        this.isLoading = false
      }
    },
    mapInventoryData (coreForms) {
      return coreForms.map((form) => {
        return {
          formId: form.id,
          templateId: form?.template_version?.core_form_template?.id,
          projectId: form.project_id,
          po: form.sequence_value || '--',
          supplier: form.forms_company_lists?.[0]?.core_tenant?.company_name || '--',
          quantity: form.forms_material_lists?.[0]?.quantity || '--',
          dateReceived: form.updated_on ? this.formatDate(form.updated_on) : '--'
        }
      })
    },
    formatDate (dateString) {
      if (!dateString) return '--'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      } catch {
        return '--'
      }
    },
    closeModal () {
      this.$emit('close')
    },
    openFormDetails (item) {
      // Open form details in new tab
      const url = `/forms/template/${item.templateId}/form/${item.formId}/view?${Config.urlKeys.projectId}=${item.projectId}&${Config.urlKeys.useSessionStorage}=true`
      window.open(url, '_blank')
    }
  }
}
</script>

<style lang="scss" scoped>
.inventory-modal-content {
  padding: 20px 0;

  .loading-state,
  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    text-align: center;
    color: #666;
    font-size: var(--text-base);
  }

  .total-quantity-section {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #e9ecef;
    display: flex;
    justify-content: flex-end;

    .total-quantity-box {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 14px;
      background-color: #f1f3f5;
      border-radius: 4px;

      .total-label {
        font-weight: 600;
        color: #333;
        font-size: var(--text-m);
      }

      .total-value {
        font-weight: 700;
        color: var(--brand-color);
        font-size: var(--text-base);
      }
    }
  }
}
</style>
